import { FormSectionHandler } from '../entity.handler';
import { AdminEditorService } from '../../admin.service';
import { FormBuilderField, FormBuilderKeyValue } from 'app/shared/components/form-builder';
import { TextField, EntityUtils, ComboField, IdNameComboValueHandlers, CheckBoxField, MemoField } from '../utils';
import { ICONS } from '../icons';
import { getEntityTypes, getTypes, CustomFieldResponse, getCustomFieldResponseFromCompany, CustomFieldResopnseToCustomFieldDefinition } from './utils';
import { IdName, CompanyResponse, CustomFields } from '@key-telematics/fleet-api-client';
import { countBy } from 'lodash';

interface EditorValues {
    tag?: string;
    name?: string;
    entityType?: IdName;
    required?: boolean;
    type?: IdName;
    values?: string;
}

export class CustomFieldDetailsSectionHandler implements FormSectionHandler<CustomFieldResponse, EditorValues> {

    type = 'form';
    icon = ICONS.customfield;
    bulk = true;

    title = this.translate('TITLE');
    description = this.translate('DESC');

    constructor(
        public admin: AdminEditorService,
        public ownerId: string,
        private creating: boolean
    ) {
    }

    private properties = ['tag', 'name', 'entityType', 'type', 'required', 'values'];

    translate(key: string, values?: Object): string {
        return this.admin.translate(`EDITORS.CUSTOMFIELD.DETAILS.${key}`, values);
    }

    applyUpdate(entity: CustomFieldResponse, payload: any): Promise<CustomFieldResponse> {
        const [ownerId, entityType, fieldId] = entity.id.split('.');
        return this.admin.getCached(`getCompany/${ownerId}`, api => api.accounts.getCompany(ownerId)).then(company => {

            const patch = CustomFieldResopnseToCustomFieldDefinition(payload);
            const customFields = JSON.parse(JSON.stringify(company.customFields));

            const idx = customFields[entityType].findIndex(x => x.id === fieldId);
            if (idx === -1) { throw new Error('Unable to find the original custom field'); }

            const item = { ...customFields[entityType][idx], ...patch };

            // changing the entityType requires moving the position of the item
            if (payload.entityType && payload.entityType !== entityType) {
                const bucket = customFields[payload.entityType] || [];
                bucket[idx] = item;
                customFields[payload.entityType] = bucket;
                customFields[entityType].splice(idx, 1);
            } else {
                customFields[entityType][idx] = item;
            }

            // check that we don't duplicate another id
            if (payload.tag) {
                const count = Object.keys(customFields).reduce((cnt, key) => {
                    return cnt + (countBy(customFields[key].map(x => x.id))[payload.tag] || 0);
                }, 0);
                if (count > 1) {
                    throw new Error('A custom field with the same ID already exists');
                }
            }

            const newId = `${ownerId}.${payload.entityType || entityType}.${item.id}`;

            return this.updateCompany(company, customFields).then(result => {
                return getCustomFieldResponseFromCompany(this.admin, result as any, newId);
            });

        });
    }

    updateCompany(company: CompanyResponse, customFields: CustomFields): Promise<any> {
        switch (company.type) {
            case 'client': return this.admin.applyUpdate(api => api.accounts.updateClientDetails(company.id, { customFields: customFields }));
            case 'vendor': return this.admin.applyUpdate(api => api.accounts.updateVendorDetails(company.id, { customFields: customFields }));
            case 'distributor': return this.admin.applyUpdate(api => api.accounts.updateDistributorDetails(company.id, { customFields: customFields }));
            default: return Promise.reject(new Error('Unknown company type'));
        }
    }

    mapEntityToEditor(entity: CustomFieldResponse): Promise<EditorValues> {
        return Promise.resolve(EntityUtils.mergeFields(this.properties, entity, {}));
    }

    mapEditorToEntity(entity: CustomFieldResponse, values: EditorValues): Promise<CustomFieldResponse> {

        if (values.values) {
            values.values = values.values.replace(/\r/g, '').split('\n').map(x => x.trim()).filter(x => x).join('\n');
        }

        if (values.tag) {
            values.tag = values.tag.toLowerCase().replace(/[\W]+/g, '');
        }

        return Promise.resolve(EntityUtils.mergeFields(this.properties, values, entity));
    }

    getFields(entity: CustomFieldResponse): Promise<FormBuilderField[]> {
        return Promise.all([
            getEntityTypes(this.admin, this.ownerId),
            getTypes(this.admin),
        ]).then(([entityTypes, types]) => {
            const typeValues = types.map<FormBuilderKeyValue>(x => ({ key: x.id, value: x.name }));
            typeValues.find(x => x.key === 'combo').fields = [
                {
                    ...MemoField('values', this.translate('FIELDS.VALUES'), true, 1, null), description: this.translate('FIELDS.VALUES_DESC'), getText(field: FormBuilderField, values: any): string {
                        const val = values[field.id];
                        return val && val.replace(/\r/g, '').split('\n').join(', ') || null;
                    },
                },
            ];
            return [
                TextField('tag', this.translate('FIELDS.ID'), true, 1, 50),
                TextField('name', this.translate('FIELDS.NAME'), true, 1, 255),
                { ...ComboField('entityType', this.translate('FIELDS.ENTITY'), true, entityTypes.map(x => ({ key: x.id, value: x.name })), IdNameComboValueHandlers), readonly: !this.creating },
                CheckBoxField('required', this.translate('FIELDS.REQUIRED'), true),
                ComboField('type', this.translate('FIELDS.TYPE'), true, typeValues, IdNameComboValueHandlers),
            ];
        }).then(fields => EntityUtils.prepareBulk(!entity, [], fields));
    }

}
